<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Button from "./Button.vue";
import Modal from "./Modal.vue";
import Select from "./Select.vue";
import { adminApi } from "../api/admin";
import { flowsApi, type FlowDefinition } from "../api/flows";
import { api } from "../api/client";
import { errorText } from "../api/errors";
import { toast } from "../composables/feedback";
import {
  flowSteps,
  startChoices,
  type StartFrom,
  type StoppedFlow,
} from "../composables/handoffSteps";
import { t } from "../i18n";

/**
 * Hands the conversation back to the assistant (it answers the contact's next message) or to a
 * published flow (it starts now, inside the 24-hour window): from its beginning, from where it
 * stopped when a person took over, or from a chosen step. The person stops attending it.
 */
const props = defineProps<{
  open: boolean;
  slug: string;
  conversationId: string;
  to: "assistant" | "flow";
  windowOpen: boolean;
  /** Where a flow stopped when someone from the team wrote in this chat. */
  stopped?: StoppedFlow | null;
}>();
const emit = defineEmits<{ close: []; done: [] }>();
const options = ref<Array<{ id: string; name: string }>>([]);
const pick = ref("");
const loading = ref(false);
const busy = ref(false);
const chosen = computed(() => options.value.find((o) => o.name === pick.value));
/** The chosen flow's published steps, read when it is chosen. */
const definition = ref<FlowDefinition | null>(null);
const from = ref<StartFrom>("beginning");
const step = ref("");
const steps = computed(() => flowSteps(definition.value));
const choices = computed(() =>
  startChoices(chosen.value?.id, props.stopped ?? null, definition.value),
);
const fromLabel = (f: StartFrom) =>
  f === "resume"
    ? t("handoff.from.resume", {
        step:
          steps.value.find((x) => x.id === props.stopped?.nodeId)?.label ?? "",
      })
    : t(`handoff.from.${f}` as never);
const fromModel = computed({
  get: () => fromLabel(from.value),
  set: (label: string) => {
    from.value =
      choices.value.find((f) => fromLabel(f) === label) ?? from.value;
  },
});
const stepModel = computed({
  get: () => steps.value.find((x) => x.id === step.value)?.label ?? "",
  set: (label: string) => {
    step.value = steps.value.find((x) => x.label === label)?.id ?? "";
  },
});
watch(
  () => (props.to === "flow" ? chosen.value?.id : undefined),
  async (flowId) => {
    definition.value = null;
    step.value = "";
    if (!flowId) return;
    try {
      const d = await flowsApi.get(props.slug, flowId);
      if (chosen.value?.id !== flowId) return;
      definition.value = d.published;
      from.value = choices.value[0]!;
      step.value = steps.value[0]?.id ?? "";
    } catch {
      // Without the steps, the flow still starts from its beginning.
    }
  },
);

watch(
  () => props.open,
  async (open) => {
    if (!open) return;
    loading.value = true;
    try {
      options.value =
        props.to === "flow"
          ? (await flowsApi.list(props.slug)).flows
              .filter((f) => f.status === "published")
              .map((f) => ({ id: f.id, name: f.name }))
          : (await adminApi.assistants(props.slug)).assistants
              .filter((a) => a.status === "active" && a.active_version_id)
              .map((a) => ({ id: a.id, name: a.name }));
      pick.value = options.value[0]?.name ?? "";
    } finally {
      loading.value = false;
    }
  },
);

async function confirm() {
  if (!chosen.value) return;
  busy.value = true;
  try {
    await api(
      `/v1/c/${props.slug}/conversations/${props.conversationId}/handoff`,
      {
        method: "POST",
        body:
          props.to === "flow"
            ? {
                to: "flow",
                flowId: chosen.value.id,
                from: from.value,
                ...(from.value === "step" ? { startAt: step.value } : {}),
              }
            : { to: "assistant", assistantId: chosen.value.id },
      },
    );
    toast(
      props.to === "flow"
        ? t("handoff.flow_done")
        : t("handoff.assistant_done"),
    );
    emit("done");
  } catch (err) {
    toast(errorText(err));
  } finally {
    busy.value = false;
  }
}
</script>
<template>
  <Modal
    :open="open"
    :title="
      to === 'flow' ? t('handoff.flow_title') : t('handoff.assistant_title')
    "
    @close="emit('close')"
  >
    <div class="modal-form">
      <p class="small muted">
        {{
          to === "flow" ? t("handoff.flow_text") : t("handoff.assistant_text")
        }}
      </p>
      <p v-if="to === 'flow' && !windowOpen" class="tpl-notice danger small">
        {{ t("handoff.window_closed") }}
      </p>
      <Select
        v-if="options.length"
        v-model="pick"
        :options="options.map((o) => o.name)"
        :label="
          to === 'flow' ? t('handoff.flow_pick') : t('handoff.assistant_pick')
        "
      />
      <template v-if="to === 'flow' && chosen && steps.length">
        <Select
          v-model="fromModel"
          :options="choices.map(fromLabel)"
          :label="t('handoff.from_label')"
        />
        <Select
          v-if="from === 'step'"
          v-model="stepModel"
          :options="steps.map((x) => x.label)"
          :label="t('handoff.step_label')"
        />
      </template>
      <p v-else-if="!options.length && !loading" class="small muted">
        {{ to === "flow" ? t("handoff.no_flows") : t("handoff.no_assistants") }}
      </p>
    </div>
    <template #footer>
      <Button variant="secondary" @click="emit('close')">{{
        t("common.cancel")
      }}</Button>
      <Button
        :icon="to === 'flow' ? 'grip' : 'sparkles'"
        :loading="busy"
        :disabled="
          !chosen ||
          (to === 'flow' && (!windowOpen || (from === 'step' && !step)))
        "
        @click="confirm"
        >{{ t("handoff.confirm") }}</Button
      >
    </template>
  </Modal>
</template>
